import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Music, Download, Copy, AlertCircle, CheckCircle2, Loader2, Upload, Link, Zap, Shield, Award, Target, DollarSign, BookOpen, Clock, TrendingUp, Check, X, Play } from 'lucide-react';

// ========================================
// SONIQLAB THEME - change colors here, everywhere updates
// 60% dominant: obsidian, charcoal, midnight
// 30% secondary: offWhite, silver, slate
// 10% accent: amber
// ========================================
const theme = {
  obsidian: '#0B0B0C',   // page background
  charcoal: '#121212',   // sections and panels
  midnight: '#0F141C',   // main cards (analyzer, report, info cards)
  raised: '#1C1C1E',     // cards inside cards, hover states
  offWhite: '#F5F5F7',   // headings and main text
  silver: '#E5E5EA',     // body text and emphasis
  slate: '#8E8E93',      // supporting text
  amber: '#FF9500',      // accent: buttons, selections, links, icons
  onAmber: '#121212',    // text on amber (white text on amber fails contrast)
  amberSoft: 'rgba(255, 149, 0, 0.14)',
  amberGlow: 'rgba(255, 149, 0, 0.35)',
  amberGlowStrong: 'rgba(255, 149, 0, 0.55)',
  border: 'rgba(142, 142, 147, 0.25)',
  borderStrong: 'rgba(142, 142, 147, 0.45)',
  success: '#30D158',
  successSoft: 'rgba(48, 209, 88, 0.15)',
  successBorder: 'rgba(48, 209, 88, 0.4)',
  danger: '#FF453A',
  dangerSoft: 'rgba(255, 69, 58, 0.15)',
  dangerBorder: 'rgba(255, 69, 58, 0.45)',
  eqBottom: 'rgba(255, 149, 0, 0.30)',
  eqTop: 'rgba(142, 142, 147, 0.10)'
};

// Load Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Card Element styling
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: theme.offWhite,
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '16px',
      '::placeholder': {
        color: theme.slate,
      },
    },
    invalid: {
      color: theme.danger,
      iconColor: theme.danger,
    },
  },
};

// EQ Background Component - Pulses on scroll!
function EQBackground() {
  const [eqHeights, setEqHeights] = useState(Array(30).fill(20));
  const lastScrollY = useRef(0);
  
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const scrollVelocity = Math.abs(scrollY - lastScrollY.current);
      const scrollPercent = scrollY / (document.documentElement.scrollHeight - window.innerHeight);
      
      if (scrollVelocity > 0) {
        const newHeights = Array(30).fill(0).map((_, i) => {
          const baseHeight = 25 + Math.random() * 15;
          const intensity = Math.min(scrollVelocity / 20, 1);
          
          let multiplier = 1;
          if (scrollPercent < 0.33) {
            multiplier = i < 10 ? 3 + intensity * 3 : 1;
          } else if (scrollPercent < 0.66) {
            multiplier = i >= 10 && i < 20 ? 3 + intensity * 3 : 1;
          } else {
            multiplier = i >= 20 ? 3 + intensity * 3 : 1;
          }
          
          return baseHeight * multiplier;
        });
        
        setEqHeights(newHeights);
        setTimeout(() => setEqHeights(Array(30).fill(20)), 400);
      }
      
      lastScrollY.current = scrollY;
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      height: '200px',
      display: 'flex',
      alignItems: 'flex-end',
      gap: '4px',
      padding: '0 2rem',
      opacity: 0.65,
      pointerEvents: 'none',
      zIndex: 0
    }}>
      {eqHeights.map((height, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            height: `${height}%`,
            background: `linear-gradient(to top, ${theme.eqBottom}, ${theme.eqTop})`,
            borderRadius: '4px 4px 0 0',
            transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: 'none'
          }}
        />
      ))}
    </div>
  );
}

function AudioAnalyzerForm() {
  const stripe = useStripe();
  const elements = useElements();
  
  // API URL - uses environment variable in production, localhost in development
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  
  const [analysisMode, setAnalysisMode] = useState('upload');
  const [songUrl, setSongUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [ownershipAcknowledged, setOwnershipAcknowledged] = useState(false);
  const [selectedDAW, setSelectedDAW] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [genreDescription, setGenreDescription] = useState('');
  const [analysis, setAnalysis] = useState('');
  const [audioFeatures, setAudioFeatures] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisStage, setAnalysisStage] = useState('');
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoChecking, setPromoChecking] = useState(false);

  const genres = [
    'Hip-hop / Rap', 'R&B / Soul', 'Pop', 'Rock', 'Metal', 'Electronic / EDM',
    'Country', 'Indie / Alternative', 'Jazz / Blues', 'Acoustic / Folk', 'Other'
  ];

  const daws = [
    'Ableton Live', 'Logic Pro', 'Pro Tools', 'FL Studio', 'Cubase',
    'Studio One', 'Reaper', 'GarageBand', 'Reason', 'Bitwig'
  ];

  // Scroll to analyzer function
const scrollToAnalyzer = () => {
  document.getElementById('analyzer-section')?.scrollIntoView({ 
    behavior: 'smooth',
    block: 'start'
  });
};

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file) => {
    const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/flac', 'audio/x-m4a', 'audio/ogg', 'audio/aac'];
    const maxSize = 50 * 1024 * 1024; // 50MB

    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|flac|m4a|ogg|aac)$/i)) {
      setError('Invalid file type. Please upload MP3, WAV, FLAC, M4A, OGG, or AAC files.');
      return;
    }

    if (file.size > maxSize) {
      setError('File too large. Maximum size is 50MB.');
      return;
    }

    setSelectedFile(file);
    setError('');
  };

  const updateProgress = (stage, progress) => {
    setAnalysisStage(stage);
    setAnalysisProgress(progress);
  };

  const applyPromo = async () => {
    setPromoError('');
    const code = promoInput.trim();
    if (!code) {
      setPromoError('Enter a promo code first.');
      return;
    }
    setPromoChecking(true);
    try {
      const response = await fetch(`${API_URL}/api/check-promo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });
      const result = await response.json();
      if (result.valid) {
        setAppliedPromo(result.code);
        setPromoInput('');
      } else {
        setPromoError(result.error || "That code isn't valid, has expired, or has already been used.");
      }
    } catch (err) {
      setPromoError("We couldn't check your promo code right now. Please try again.");
    } finally {
      setPromoChecking(false);
    }
  };

  const removePromo = () => {
    setAppliedPromo('');
    setPromoError('');
  };

  const handleAnalyze = async () => {
  setError('');
  setAnalysis('');
  setAudioFeatures(null);
  setCopySuccess(false);
  setAnalysisProgress(0);
  setAnalysisStage('');

  // Validation
  if (analysisMode === 'url' && !songUrl.trim()) {
    setError('Please enter a song URL');
    return;
  }

  if (analysisMode === 'upload' && !selectedFile) {
    setError('Please select an audio file to upload');
    return;
  }

  if (!ownershipAcknowledged) {
    setError('Please acknowledge that you own the song');
    return;
  }

  if (!selectedDAW) {
    setError('Please select your DAW software');
    return;
  }

  const usingPromo = Boolean(appliedPromo);

  if (!usingPromo && (!stripe || !elements)) {
    setError('Payment system not loaded. Please refresh the page.');
    return;
  }

  setIsAnalyzing(true);

  try {
    let paymentIntentId = null;
    let cardElement = null;

    // Paying by card (skipped when a promo code is applied)
    if (!usingPromo) {
      // STEP 1: Create payment intent
      updateProgress('Creating payment...', 10);
      const paymentResponse = await fetch(`${API_URL}/api/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const paymentData = await paymentResponse.json();
    
      if (!paymentData.clientSecret) {
        throw new Error('Failed to create payment intent');
      }

      // STEP 2: Confirm payment with Stripe
      updateProgress('Processing payment...', 30);
      cardElement = elements.getElement(CardElement);
    
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        paymentData.clientSecret,
        {
          payment_method: { card: cardElement }
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (paymentIntent.status !== 'succeeded') {
        throw new Error('Payment was not successful. Please try again.');
      }
      paymentIntentId = paymentIntent.id;
    }

    // STEP 3: Upload and analyze (payment or promo code verified by the server)
    updateProgress('Uploading audio...', 50);
    const formData = new FormData();
    formData.append('audioFile', selectedFile);
    formData.append('daw', selectedDAW);
    if (selectedGenre) formData.append('genre', selectedGenre);
    if (selectedGenre === 'Other' && genreDescription.trim()) {
      formData.append('genreDescription', genreDescription.trim());
    }
    if (usingPromo) {
      formData.append('promoCode', appliedPromo);
    } else {
      formData.append('paymentIntentId', paymentIntentId);
    }

    updateProgress('Analyzing audio...', 70);
    const response = await fetch(`${API_URL}/api/analyze-upload`, {
      method: 'POST',
      body: formData
    });

    const result = await response.json();

    if (result.error) {
      throw new Error(result.error);
    }

    // STEP 4: Success!
    updateProgress('Complete!', 100);
    setAnalysis(result.analysis);
    setAudioFeatures(result.features);
    
    // Clear card, or clear the promo code (it's now used)
    if (cardElement) cardElement.clear();
    if (usingPromo) {
      setAppliedPromo('');
      setPromoOpen(false);
    }

    // Scroll to results
    setTimeout(() => {
      document.getElementById('results-section')?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }, 500);

  } catch (err) {
    console.error('Analysis error:', err);
    setError(err.message || 'Analysis failed. Please try again.');
    // If the promo code turned out to be invalid or used, switch back to card payment
    if (usingPromo && /isn't valid|already being used/.test(err.message || '')) {
      setAppliedPromo('');
    }
  } finally {
    setIsAnalyzing(false);
  }
};

  const handleCopy = () => {
    navigator.clipboard.writeText(analysis);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([analysis], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'audio-engineer-analysis.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: theme.obsidian,
      fontFamily: 'system-ui, -apple-system, sans-serif'
    },
    section: {
      padding: '4rem 2rem',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    sectionNarrow: {
      padding: '4rem 2rem',
      maxWidth: '1000px',
      margin: '0 auto'
    },
    card: {
      background: theme.midnight,
      backdropFilter: 'blur(10px)',
      borderRadius: '1rem',
      padding: '2rem',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
      border: `1px solid ${theme.border}`
    },
    modeToggle: (active) => ({
      flex: 1,
      padding: '1rem',
      borderRadius: '0.75rem',
      border: active ? `2px solid ${theme.amber}` : `2px solid ${theme.border}`,
      background: active ? theme.raised : theme.obsidian,
      color: active ? theme.offWhite : theme.silver,
      fontWeight: active ? '600' : '500',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem'
    }),
    dropZone: {
  borderRadius: '1rem',
  padding: '3rem 2rem',
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s',
  marginBottom: '1.5rem'
},
    label: {
      display: 'block',
      color: theme.offWhite,
      fontWeight: '600',
      marginBottom: '0.5rem'
    },
    input: {
      width: '100%',
      padding: '0.75rem 1rem',
      background: theme.obsidian,
      border: `1px solid ${theme.border}`,
      borderRadius: '0.5rem',
      color: theme.offWhite,
      fontSize: '1rem',
      boxSizing: 'border-box'
    },
    cardElementContainer: {
      padding: '0.75rem 1rem',
      background: theme.obsidian,
      border: `1px solid ${theme.border}`,
      borderRadius: '0.5rem',
      marginBottom: '1rem'
    },
    dawButton: (selected) => ({
      padding: '0.5rem 1rem',
      borderRadius: '0.5rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s',
      background: selected ? theme.amber : theme.obsidian,
      color: selected ? theme.onAmber : theme.silver,
      border: selected ? 'none' : `1px solid ${theme.border}`,
      transform: selected ? 'scale(1.05)' : 'scale(1)',
      boxShadow: selected ? `0 10px 15px -3px ${theme.amberGlow}` : 'none'
    }),
    button: {
      width: '100%',
      padding: '1rem',
      background: theme.amber,
      color: theme.onAmber,
      fontWeight: 'bold',
      borderRadius: '0.5rem',
      border: 'none',
      cursor: 'pointer',
      fontSize: '1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem'
    },
    error: {
      background: theme.dangerSoft,
      border: `1px solid ${theme.dangerBorder}`,
      borderRadius: '0.5rem',
      padding: '1rem',
      color: theme.offWhite,
      display: 'flex',
      gap: '0.75rem',
      marginBottom: '1.5rem'
    },
    progressBar: {
      width: '100%',
      height: '8px',
      background: theme.obsidian,
      borderRadius: '999px',
      overflow: 'hidden',
      marginTop: '1rem'
    },
    progressFill: {
      height: '100%',
      background: theme.amber,
      transition: 'width 0.3s ease',
      borderRadius: '999px'
    },
    preText: {
      whiteSpace: 'pre-wrap',
      color: theme.offWhite,
      fontFamily: 'monospace',
      fontSize: '0.875rem',
      lineHeight: '1.5'
    }
  };

  return (
    <div style={styles.container}>
      <style>{`
  @keyframes pulse {
    0%, 100% {
      box-shadow: 0 10px 40px -10px ${theme.amberGlow};
    }
    50% {
      box-shadow: 0 10px 50px -5px ${theme.amberGlowStrong};
    }
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  .spin {
    animation: spin 1s linear infinite;
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .fade-in-up {
    animation: fadeInUp 0.6s ease-out;
  }
`}</style>
{/* EQ Background */}
<EQBackground />

{/* Sticky Try Now Button */}
<button
  onClick={scrollToAnalyzer}
  style={{
    position: 'fixed',
    top: '2rem',
    right: '2rem',
    padding: '0.75rem 1.5rem',
    background: theme.amber,
    border: 'none',
    borderRadius: '9999px',
    color: theme.onAmber,
    fontWeight: 'bold',
    cursor: 'pointer',
    zIndex: 1000,
    boxShadow: `0 10px 40px -10px ${theme.amberGlow}`,
    transition: 'all 0.3s',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '1rem',
    animation: 'pulse 2s ease-in-out infinite'
  }}
  onMouseOver={(e) => {
    e.currentTarget.style.transform = 'translateY(-2px)';
    e.currentTarget.style.boxShadow = `0 15px 50px -10px ${theme.amberGlowStrong}`;
  }}
  onMouseOut={(e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = `0 10px 40px -10px ${theme.amberGlow}`;
  }}
>
  <Play size={20} />
  Try Now - $2
</button>
      {/* Hero Section */}
      <div style={{...styles.section, textAlign: 'center', paddingTop: '3rem', paddingBottom: '2rem'}}>
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap'}}>
          <Music size={56} color={theme.amber} />
          <h1 style={{fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 'bold', color: theme.offWhite, margin: 0}}>
            SoniqLab
          </h1>
        </div>
        <p style={{color: theme.offWhite, fontSize: 'clamp(1rem, 2.5vw, 1.25rem)', marginBottom: '0.5rem'}}>
          Professional AI Mixing & Mastering Analysis
        </p>
        <p style={{color: theme.silver, fontSize: 'clamp(0.875rem, 2vw, 1rem)', maxWidth: '800px', margin: '1.5rem auto 0', lineHeight: '1.6'}}>
          Get studio-grade mixing and mastering insights tailored to your DAW. 
          Upload your track, receive professional analysis, and elevate your sound—all for just $2 per song.
        </p>
        <div style={{marginTop: '2rem', padding: '1rem', background: theme.charcoal, borderRadius: '0.75rem', display: 'inline-block'}}>
          <p style={{color: theme.slate, fontSize: '0.875rem', margin: 0}}>
            💡 <strong>Our Mission:</strong> SoniqLab democratizes professional audio engineering knowledge. 
            Keep creative control while learning from AI-powered insights that would cost hundreds with a traditional engineer.
          </p>
        </div>
      </div>

      {/* Analysis Tool Section */}
      <div style={styles.sectionNarrow} id="analyzer-section">
        <div style={styles.card}>
          {/* Analysis Mode Toggle */}
          <div style={{marginBottom: '2rem'}}>
            <label style={{...styles.label, marginBottom: '1rem'}}>Choose Analysis Method</label>
            <div style={{display: 'flex', gap: '1rem', flexWrap: 'wrap'}}>
              <button
                onClick={() => setAnalysisMode('upload')}
                style={styles.modeToggle(analysisMode === 'upload')}
              >
                <Upload size={20} />
                Upload File (Recommended)
              </button>
              <button
                onClick={() => setAnalysisMode('url')}
                style={styles.modeToggle(analysisMode === 'url')}
              >
                <Link size={20} />
                Paste URL
              </button>
            </div>
            <p style={{fontSize: '0.75rem', color: theme.slate, marginTop: '0.75rem', textAlign: 'center'}}>
              {analysisMode === 'upload' 
                ? '⚡ Fastest & most reliable • Better quality analysis' 
                : '🌐 YouTube, SoundCloud, Vimeo, etc.'}
            </p>
          </div>

          {/* File Upload or URL Input */}
          {analysisMode === 'upload' ? (
            <div style={{marginBottom: '1.5rem'}}>
              <label style={styles.label}>Upload Audio File</label>
              <div
                style={{
                   ...styles.dropZone,
                 border: isDragging ? `3px dashed ${theme.amber}` : `2px dashed ${theme.borderStrong}`,
                 background: isDragging ? theme.amberSoft : theme.obsidian,
                  boxShadow: isDragging ? `0 0 30px ${theme.amberGlow}` : 'none'
                }}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => document.getElementById('fileInput').click()}
              >
                <Upload size={48} color={isDragging ? theme.offWhite : theme.slate} style={{margin: '0 auto 1rem'}} />
                <p style={{color: theme.offWhite, fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem'}}>
                  {selectedFile ? selectedFile.name : 'Drop your audio file here'}
                </p>
                <p style={{color: theme.silver, fontSize: '0.875rem'}}>
                  {selectedFile 
                    ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB • Click to change`
                    : 'or click to browse • MP3, WAV, FLAC, M4A, OGG, AAC • Max 50MB'}
                </p>
              </div>
              <input
                id="fileInput"
                type="file"
                accept=".mp3,.wav,.flac,.m4a,.ogg,.aac,audio/*"
                onChange={handleFileSelect}
                style={{display: 'none'}}
              />
            </div>
          ) : (
            <div style={{marginBottom: '1.5rem'}}>
              <label style={styles.label}>Song URL</label>
              <input
                type="text"
                value={songUrl}
                onChange={(e) => setSongUrl(e.target.value)}
                placeholder="https://soundcloud.com/yourtrack or https://youtube.com/..."
                style={styles.input}
              />
              <p style={{fontSize: '0.75rem', color: theme.silver, marginTop: '0.5rem'}}>
                Supported: YouTube, SoundCloud, Google Drive, Dropbox, Vimeo
              </p>
            </div>
          )}

          {/* Ownership Acknowledgment */}
          <div style={{marginBottom: '1.5rem'}}>
            <label style={{display: 'flex', alignItems: 'start', gap: '0.75rem', cursor: 'pointer', color: theme.offWhite}}>
              <input
                type="checkbox"
                checked={ownershipAcknowledged}
                onChange={(e) => setOwnershipAcknowledged(e.target.checked)}
                style={{marginTop: '0.25rem', width: '1.25rem', height: '1.25rem', cursor: 'pointer', flexShrink: 0}}
              />
              <span style={{fontSize: 'clamp(0.875rem, 2vw, 1rem)'}}>
                I acknowledge that I own this song and have the rights to analyze it
              </span>
            </label>
          </div>

          {/* DAW Selection */}
          <div style={{marginBottom: '1.5rem'}}>
            <label style={styles.label}>Select Your DAW</label>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem'}}>
              {daws.map((daw) => (
                <button
                  key={daw}
                  onClick={() => setSelectedDAW(daw)}
                  style={styles.dawButton(selectedDAW === daw)}
                >
                  {daw}
                </button>
              ))}
            </div>
          </div>

          {/* Genre Selection (optional) */}
          <div style={{marginBottom: '1.5rem'}}>
            <label style={styles.label}>Select Your Genre <span style={{fontWeight: 'normal', color: theme.slate}}>(optional)</span></label>
            <p style={{fontSize: '0.8rem', color: theme.silver, margin: '0 0 0.75rem'}}>
              Helps tailor your recommendations to your genre's conventions.
            </p>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem'}}>
              {genres.map((genre) => (
                <button
                  key={genre}
                  onClick={() => setSelectedGenre(selectedGenre === genre ? '' : genre)}
                  style={styles.dawButton(selectedGenre === genre)}
                  aria-pressed={selectedGenre === genre}
                >
                  {genre}
                </button>
              ))}
            </div>
            {selectedGenre === 'Other' && (
              <input
                type="text"
                value={genreDescription}
                onChange={(e) => setGenreDescription(e.target.value)}
                maxLength={40}
                placeholder="Describe your style, like lo-fi bedroom pop"
                aria-label="Describe your style (optional)"
                style={{...styles.input, marginTop: '0.75rem'}}
              />
            )}
          </div>

{/* What You Get - Value Showcase */}
<div style={{
  marginTop: '0rem',
  marginBottom: '2rem',
  background: theme.charcoal,
  borderRadius: '1rem',
  padding: '1.5rem',
  border: `2px solid ${theme.borderStrong}`,
  boxShadow: 'none'
}}>
  <div style={{textAlign: 'center', marginBottom: '1.5rem'}}>
    <h3 style={{
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: theme.offWhite,
      marginBottom: '0.5rem'
    }}>
      What You'll Receive for $2
    </h3>
    <p style={{color: theme.silver, fontSize: '0.875rem'}}>
      The most comprehensive AI music analysis available
    </p>
  </div>

  {/* Feature Cards */}
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginBottom: '1.5rem'
  }}>
    {/* Feature 1 - 100-Band Analysis */}
    <div style={{
      background: theme.raised,
      borderRadius: '0.5rem',
      padding: '1rem',
      border: `1px solid ${theme.border}`
    }}>
      <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem'}}>
        <Target size={20} color={theme.amber} />
        <h4 style={{color: theme.offWhite, fontWeight: 'bold', fontSize: '0.95rem', margin: 0}}>
          100-Band Frequency Analysis
        </h4>
      </div>
      <p style={{color: theme.slate, fontSize: '0.875rem', lineHeight: '1.5', margin: 0}}>
        Ultra-precise analysis from 20Hz to 20kHz. <strong style={{color: theme.offWhite}}>3.3x more detailed</strong> than standard 30-band analysis.
      </p>
    </div>

    {/* Feature 2 - 30-50 Recommendations */}
    <div style={{
      background: theme.raised,
      borderRadius: '0.5rem',
      padding: '1rem',
      border: `1px solid ${theme.border}`
    }}>
       <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem'}}>
    <BookOpen size={20} color={theme.amber} />
    <h4 style={{color: theme.offWhite, fontWeight: 'bold', fontSize: '0.95rem', margin: 0}}>
      30-50 Surgical Recommendations
    </h4>
  </div>
  <p style={{color: theme.slate, fontSize: '0.875rem', lineHeight: '1.5', margin: 0}}>
    Exact Hz/dB values and step-by-step {selectedDAW ? <strong style={{color: theme.offWhite}}>{selectedDAW}</strong> : 'DAW'} instructions. <strong style={{color: theme.offWhite}}>More issues found = more fixes provided.</strong> Quality mixes get focused feedback.
      </p>
    </div>

    {/* Feature 3 - Real Audio Analysis */}
<div style={{
  background: theme.raised,
  borderRadius: '0.5rem',
  padding: '1rem',
  border: `1px solid ${theme.border}`
}}>
  <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem'}}>
    <TrendingUp size={20} color={theme.amber} />
    <h4 style={{color: theme.offWhite, fontWeight: 'bold', fontSize: '0.95rem', margin: 0}}>
      Actual Audio Analysis
    </h4>
  </div>
  <p style={{color: theme.slate, fontSize: '0.875rem', lineHeight: '1.5', margin: 0}}>
    We <strong style={{color: theme.offWhite}}>actually analyze your audio file</strong> - ChatGPT can't! Get real measurements from YOUR track, not assumptions.
  </p>
</div>

    {/* Feature 4 - Vocal & Rhythm */}
    <div style={{
      background: theme.raised,
      borderRadius: '0.5rem',
      padding: '1rem',
      border: `1px solid ${theme.border}`
    }}>
      <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem'}}>
        <Music size={20} color={theme.amber} />
        <h4 style={{color: theme.offWhite, fontWeight: 'bold', fontSize: '0.95rem', margin: 0}}>
          Vocal & Rhythm Analysis
        </h4>
      </div>
      <p style={{color: theme.slate, fontSize: '0.875rem', lineHeight: '1.5', margin: 0}}>
        Sibilance detection, vocal presence, BPM stability, and <strong style={{color: theme.offWhite}}>transient sharpness</strong> analysis.
      </p>
    </div>

    {/* Feature 5 - Stereo by Frequency */}
    <div style={{
      background: theme.raised,
      borderRadius: '0.5rem',
      padding: '1rem',
      border: `1px solid ${theme.border}`
    }}>
      <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem'}}>
        <Zap size={20} color={theme.amber} />
        <h4 style={{color: theme.offWhite, fontWeight: 'bold', fontSize: '0.95rem', margin: 0}}>
          Stereo Width by Frequency
        </h4>
      </div>
      <p style={{color: theme.slate, fontSize: '0.875rem', lineHeight: '1.5', margin: 0}}>
        Bass mono check, mid-range placement, and <strong style={{color: theme.offWhite}}>high-frequency stereo</strong> spread analysis.
      </p>
    </div>

    {/* Feature 6 - PDF Report */}
    <div style={{
      background: theme.raised,
      borderRadius: '0.5rem',
      padding: '1rem',
      border: `1px solid ${theme.border}`
    }}>
      <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem'}}>
        <Download size={20} color={theme.amber} />
        <h4 style={{color: theme.offWhite, fontWeight: 'bold', fontSize: '0.95rem', margin: 0}}>
          DAW-Specific Presets
        </h4>
      </div>
      <p style={{color: theme.slate, fontSize: '0.875rem', lineHeight: '1.5', margin: 0}}>
        <strong style={{color: theme.offWhite}}>Downloadable</strong> preset files that automatically load all recommended EQ, compression, and effects settings directly into your DAW - no manual adjustments needed!
      </p>
    </div>
  </div>

  {/* Value Statement */}
  <div style={{
    textAlign: 'center',
    padding: '1rem',
    background: theme.raised,
    borderRadius: '0.5rem',
    border: `1px solid ${theme.border}`
  }}>
    <p style={{
      color: theme.offWhite,
      fontSize: '0.875rem',
      margin: 0,
      lineHeight: '1.6'
    }}>
      <strong style={{color: theme.slate}}>Professional studio analysis normally costs $100-500.</strong><br/>
      Get the same level of detail powered by AI for just <strong style={{color: theme.amber, fontSize: '1.125rem'}}>$2</strong>.
    </p>
  </div>
</div>

          {/* Stripe Payment Section - ALWAYS SHOW */}
          <div style={{background: theme.obsidian, borderRadius: '1rem', padding: '1.5rem', border: `1px solid ${theme.border}`, marginBottom: '1.5rem'}}>
            <h3 style={{color: theme.offWhite, fontWeight: '600', marginBottom: '1rem'}}>
              {appliedPromo ? 'Payment: free with promo code' : 'Payment ($2.00)'}
            </h3>

            {appliedPromo ? (
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', padding: '0.75rem 1rem', background: theme.successSoft, border: `1px solid ${theme.successBorder}`, borderRadius: '0.5rem'}}>
                <span style={{color: theme.offWhite, display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <CheckCircle2 size={18} color={theme.success} />
                  Free analysis unlocked with code <strong>{appliedPromo}</strong>
                </span>
                <button
                  onClick={removePromo}
                  disabled={isAnalyzing}
                  style={{background: 'none', border: 'none', color: theme.amber, cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: '0.875rem'}}
                >
                  Remove code
                </button>
              </div>
            ) : (
              <>
                <div style={styles.cardElementContainer}>
                  <CardElement options={CARD_ELEMENT_OPTIONS} />
                </div>
                <p style={{fontSize: '0.75rem', color: theme.slate, marginTop: '0.5rem'}}>
                  🔒 Secure payment processing by Stripe. Your card information is never stored.
                </p>

                {!promoOpen ? (
                  <button
                    onClick={() => setPromoOpen(true)}
                    style={{background: 'none', border: 'none', color: theme.amber, cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: '0.875rem', marginTop: '0.5rem'}}
                  >
                    Have a promo code?
                  </button>
                ) : (
                  <div style={{marginTop: '0.75rem'}}>
                    <div style={{display: 'flex', gap: '0.5rem'}}>
                      <input
                        type="text"
                        value={promoInput}
                        onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(''); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') applyPromo(); }}
                        maxLength={40}
                        placeholder="Promo code"
                        aria-label="Promo code"
                        style={{...styles.input, textTransform: 'uppercase', letterSpacing: '0.05em'}}
                      />
                      <button
                        onClick={applyPromo}
                        disabled={promoChecking}
                        style={{padding: '0 1.25rem', background: theme.raised, color: theme.offWhite, border: `1px solid ${theme.borderStrong}`, borderRadius: '0.5rem', cursor: promoChecking ? 'wait' : 'pointer', fontWeight: '600', whiteSpace: 'nowrap'}}
                      >
                        {promoChecking ? 'Checking...' : 'Apply'}
                      </button>
                    </div>
                    {promoError && (
                      <p role="alert" style={{color: theme.danger, fontSize: '0.8rem', margin: '0.5rem 0 0'}}>{promoError}</p>
                    )}
                  </div>
                )}
              </>
            )}
            <p style={{fontSize: '0.75rem', color: theme.slate, marginTop: '0.25rem'}}>
              Your audio is processed as described in our <a href="/privacy.html" style={{color: theme.amber}}>Privacy Policy</a>.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div style={styles.error}>
              <AlertCircle size={20} style={{flexShrink: 0, marginTop: '0.125rem'}} />
              <p style={{margin: 0}}>{error}</p>
            </div>
          )}

          {/* Analyze Button */}
          <button
            onClick={handleAnalyze}
            disabled={isAnalyzing || (!appliedPromo && !stripe)}
            style={{
              ...styles.button,
              opacity: (isAnalyzing || (!appliedPromo && !stripe)) ? 0.5 : 1,
              cursor: (isAnalyzing || (!appliedPromo && !stripe)) ? 'not-allowed' : 'pointer'
            }}
          >
            {isAnalyzing ? (
              <>
                <Loader2 size={20} className="spin" />
                {analysisStage || 'Processing...'}
              </>
            ) : (
              appliedPromo ? 'Analyze My Song (Free)' : 'Pay $2.00 & Analyze My Song'
            )}
          </button>

          {/* Progress Bar */}
          {isAnalyzing && (
            <div style={{marginTop: '1rem'}}>
              <div style={styles.progressBar}>
                <div style={{...styles.progressFill, width: `${analysisProgress}%`}}></div>
              </div>
              <p style={{fontSize: '0.875rem', color: theme.slate, textAlign: 'center', marginTop: '0.5rem'}}>
                {analysisProgress}% Complete
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Analysis Output */}
      {/* Analysis Output - REPLACE FROM HERE */}
      {analysis && (
        <div id="results-section" style={{
          marginTop: '4rem',
          background: theme.midnight,
          backdropFilter: 'blur(10px)',
          borderRadius: '1.5rem',
          padding: '2rem',
          border: `1px solid ${theme.border}`,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
        }}>
          {/* Header */}
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
              <CheckCircle2 size={32} color={theme.success} />
              <h2 style={{
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontWeight: 'bold',
                color: theme.offWhite,
                margin: 0
              }}>
                Analysis Complete
              </h2>
            </div>
            
            {/* Action Buttons */}
            <div style={{display: 'flex', gap: '0.75rem', flexWrap: 'wrap'}}>
              <button
                onClick={handleCopy}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1rem',
                  background: copySuccess ? theme.successSoft : theme.raised,
                  border: copySuccess ? `1px solid ${theme.successBorder}` : `1px solid ${theme.border}`,
                  borderRadius: '0.5rem',
                  color: copySuccess ? theme.success : theme.offWhite,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontWeight: '500'
                }}
                onMouseOver={(e) => {
                  if (!copySuccess) {
                    e.currentTarget.style.background = theme.amberSoft;
                    e.currentTarget.style.color = theme.offWhite;
                  }
                }}
                onMouseOut={(e) => {
                  if (!copySuccess) {
                    e.currentTarget.style.background = theme.raised;
                    e.currentTarget.style.color = theme.offWhite;
                  }
                }}
              >
                {copySuccess ? (
                  <>
                    <CheckCircle2 size={16} color={theme.success} />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy size={16} />
                    <span>Copy</span>
                  </>
                )}
              </button>
              
              <button
                onClick={handleDownload}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1rem',
                  background: theme.raised,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '0.5rem',
                  color: theme.offWhite,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontWeight: '500'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = theme.amberSoft;
                  e.currentTarget.style.color = theme.offWhite;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = theme.raised;
                  e.currentTarget.style.color = theme.offWhite;
                }}
              >
                <Download size={16} />
                <span>Download TXT</span>
              </button>
            </div>
          </div>

          {/* Audio Features Summary */}
          {audioFeatures && (
            <div style={{
              marginBottom: '2rem',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem'
            }}>
              <div style={{
                background: theme.raised,
                borderRadius: '0.75rem',
                padding: '1rem',
                border: `1px solid ${theme.border}`
              }}>
                <div style={{color: theme.silver, fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: '500'}}>
                  Loudness
                </div>
                <div style={{color: theme.offWhite, fontSize: '1.5rem', fontWeight: 'bold'}}>
                  {audioFeatures.integratedLoudness ? audioFeatures.integratedLoudness.toFixed(1) : 'N/A'} LUFS
                </div>
              </div>
              <div style={{
                background: theme.raised,
                borderRadius: '0.75rem',
                padding: '1rem',
                border: `1px solid ${theme.border}`
              }}>
                <div style={{color: theme.silver, fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: '500'}}>
                  Stereo Width
                </div>
                <div style={{color: theme.offWhite, fontSize: '1.5rem', fontWeight: 'bold'}}>
                  {audioFeatures.stereoWidth !== null && audioFeatures.stereoWidth !== undefined ? (audioFeatures.stereoWidth * 100).toFixed(0) : 'N/A'}%
                </div>
              </div>
              <div style={{
                background: theme.raised,
                borderRadius: '0.75rem',
                padding: '1rem',
                border: `1px solid ${theme.border}`
              }}>
                <div style={{color: theme.silver, fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: '500'}}>
                  Dynamic Range
                </div>
                <div style={{color: theme.offWhite, fontSize: '1.5rem', fontWeight: 'bold'}}>
                  {audioFeatures.dynamicRange ? audioFeatures.dynamicRange.toFixed(1) : 'N/A'} dB
                </div>
              </div>
              <div style={{
                background: theme.raised,
                borderRadius: '0.75rem',
                padding: '1rem',
                border: `1px solid ${theme.border}`
              }}>
                <div style={{color: theme.silver, fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: '500'}}>
                  Phase Correlation
                </div>
                <div style={{color: theme.offWhite, fontSize: '1.5rem', fontWeight: 'bold'}}>
                  {audioFeatures.phaseCorrelation ? audioFeatures.phaseCorrelation.toFixed(2) : 'N/A'}
                </div>
              </div>
            </div>
          )}

          {/* Charts Display */}
          {/*chartUrls && (
            <div style={{marginBottom: '2rem'}}>
              {/* Frequency Chart 
              <div style={{
                background: 'linear-gradient(135deg, rgba(31, 41, 55, 0.6) 0%, rgba(88, 28, 135, 0.3) 100%)',
                borderRadius: '1rem',
                padding: '1.5rem',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                marginBottom: '1.5rem'
              }}>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  color: '#e9d5ff',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Music size={20} color="#c084fc" />
                  Frequency Analysis
                </h3>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '0.5rem',
                  padding: '0.5rem'
                }}>
                  <img 
                    src={`${API_URL}${chartUrls.frequency}`}
                    alt="Frequency Spectrum"
                    style={{width: '100%', borderRadius: '0.5rem', display: 'block'}}
                  />
                </div>
                <p style={{
                  color: '#d8b4fe',
                  fontSize: '0.875rem',
                  marginTop: '0.75rem',
                  lineHeight: '1.5'
                }}>
                  Shows the balance of frequencies across 30 bands. Blue = good, Red = too loud, Yellow = too quiet.
                </p>
              </div>

              {/* Stereo Chart 
              <div style={{
                background: 'linear-gradient(135deg, rgba(31, 41, 55, 0.6) 0%, rgba(88, 28, 135, 0.3) 100%)',
                borderRadius: '1rem',
                padding: '1.5rem',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                marginBottom: '1.5rem'
              }}>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  color: '#e9d5ff',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Zap size={20} color="#c084fc" />
                  Stereo Field Analysis
                </h3>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '0.5rem',
                  padding: '0.5rem'
                }}>
                  <img 
                    src={`${API_URL}${chartUrls.stereo}`}
                    alt="Stereo Width"
                    style={{width: '100%', borderRadius: '0.5rem', display: 'block'}}
                  />
                </div>
                <p style={{
                  color: '#d8b4fe',
                  fontSize: '0.875rem',
                  marginTop: '0.75rem',
                  lineHeight: '1.5'
                }}>
                  Your stereo width compared to commercial tracks. Target: 50-70%.
                </p>
              </div>

              {/* Loudness Chart 
              <div style={{
                background: 'linear-gradient(135deg, rgba(31, 41, 55, 0.6) 0%, rgba(88, 28, 135, 0.3) 100%)',
                borderRadius: '1rem',
                padding: '1.5rem',
                border: '1px solid rgba(168, 85, 247, 0.3)'
              }}>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  color: '#e9d5ff',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Target size={20} color="#c084fc" />
                  Loudness Analysis
                </h3>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '0.5rem',
                  padding: '0.5rem'
                }}>
                  <img 
                    src={`${API_URL}${chartUrls.loudness}`}
                    alt="Loudness Comparison"
                    style={{width: '100%', borderRadius: '0.5rem', display: 'block'}}
                  />
                </div>
                <p style={{
                  color: '#d8b4fe',
                  fontSize: '0.875rem',
                  marginTop: '0.75rem',
                  lineHeight: '1.5'
                }}>
                  Your loudness compared to genre targets and streaming standards.
                </p>
              </div>
            </div>
          )}
        */}

          {/* Divider 
          <div style={{
            borderTop: `1px solid ${theme.border}`,
            margin: '2rem 0'
          }}></div>
          */}

          {/* Analysis Text */}
<div>
  <h3 style={{
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: theme.offWhite,
    marginBottom: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  }}>
    <BookOpen size={24} color={theme.amber} />
    Detailed Recommendations
  </h3>
  <div style={{
    background: theme.charcoal,
    borderRadius: '1rem',
    padding: '1.5rem',
    border: `1px solid ${theme.border}`
  }}>
    <style>{`
      /* Enhanced Analysis Styling */
      .analysis-content h2 {
        font-size: 1.75rem !important;
        font-weight: bold !important;
        color: ${theme.offWhite} !important;
        margin-top: 2.5rem !important;
        margin-bottom: 1.5rem !important;
        padding-bottom: 0.75rem !important;
        border-bottom: 2px solid ${theme.border} !important;
      }
      
      .analysis-content h3 {
        font-size: 1.25rem !important;
        font-weight: bold !important;
        color: ${theme.offWhite} !important;
        margin-top: 2rem !important;
        margin-bottom: 1rem !important;
        padding: 0.75rem 1rem !important;
        background: ${theme.raised} !important;
        border-left: 4px solid ${theme.amber} !important;
        border-radius: 0.25rem !important;
      }
      
      .analysis-content strong {
        color: ${theme.offWhite} !important;
        font-weight: 600 !important;
      }
      
      .analysis-content p {
        margin: 0.75rem 0 !important;
      }
    `}</style>
    <div 
      className="analysis-content"
      style={{
        whiteSpace: 'pre-wrap',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        color: theme.offWhite,
        lineHeight: '1.7',
        fontSize: 'clamp(0.875rem, 2vw, 1rem)',
        margin: 0
      }}
      dangerouslySetInnerHTML={{
        __html: analysis
          // Escape HTML first so nothing in a report can act as code on the page
          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
          // Convert ## headers to <h2>
          .replace(/^## (.+)$/gm, '<h2>$1</h2>')
          // Convert ### headers (numbered recommendations) to <h3>
          .replace(/^### (.+)$/gm, '<h3>$1</h3>')
          // Highlight PROBLEM/FIX/RESULT keywords
          .replace(/\*\*PROBLEM:\*\*/g, `<strong style="color: ${theme.amber};">⚠️ PROBLEM:</strong>`)
          .replace(/\*\*LISTENER IMPACT:\*\*/g, `<strong style="color: ${theme.silver};">🎧 LISTENER IMPACT:</strong>`)
          .replace(/\*\*Logic Pro FIX:\*\*/g, `<strong style="color: ${theme.success};">🔧 Logic Pro FIX:</strong>`)
          .replace(/\*\*Ableton Live FIX:\*\*/g, `<strong style="color: ${theme.success};">🔧 Ableton Live FIX:</strong>`)
          .replace(/\*\*Pro Tools FIX:\*\*/g, `<strong style="color: ${theme.success};">🔧 Pro Tools FIX:</strong>`)
          .replace(/\*\*FL Studio FIX:\*\*/g, `<strong style="color: ${theme.success};">🔧 FL Studio FIX:</strong>`)
          .replace(/\*\*Cubase FIX:\*\*/g, `<strong style="color: ${theme.success};">🔧 Cubase FIX:</strong>`)
          .replace(/\*\*Studio One FIX:\*\*/g, `<strong style="color: ${theme.success};">🔧 Studio One FIX:</strong>`)
          .replace(/\*\*Reaper FIX:\*\*/g, `<strong style="color: ${theme.success};">🔧 Reaper FIX:</strong>`)
          .replace(/\*\*GarageBand FIX:\*\*/g, `<strong style="color: ${theme.success};">🔧 GarageBand FIX:</strong>`)
          .replace(/\*\*Reason FIX:\*\*/g, `<strong style="color: ${theme.success};">🔧 Reason FIX:</strong>`)
          .replace(/\*\*Bitwig FIX:\*\*/g, `<strong style="color: ${theme.success};">🔧 Bitwig FIX:</strong>`)
          .replace(/\*\*EXPECTED RESULT:\*\*/g, `<strong style="color: ${theme.offWhite};">✨ EXPECTED RESULT:</strong>`)
          // Any remaining **bold** text
          .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
          // Convert line breaks to <p> tags for better spacing
          .split('\n\n').map(para => `<p>${para}</p>`).join('')
      }}
    />
  </div>
</div>

        </div>
      )}
      {/* Analysis Output - REPLACE TO HERE */}

      {/* How It Works Section */}
      <div style={styles.section} id="how-it-works">
        <h2 style={{fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 'bold', color: theme.offWhite, textAlign: 'center', marginBottom: '3rem'}}>
          How It Works
        </h2>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem'}}>
          {[
            { icon: Upload, title: '1. Upload Your Track', desc: 'Upload your audio file or paste a URL from YouTube, SoundCloud, or other platforms.' },
            { icon: Zap, title: '2. Deep Audio Analysis', desc: 'Automatically measures 100 frequency bands plus vocals, rhythm, stereo field, dynamics, and streaming optimization - based on YOUR actual audio file.' },
            { icon: Target, title: '3. Get DAW-Specific Feedback', desc: 'Receive detailed mixing and mastering instructions tailored to your specific DAW with exact settings.' },
            { icon: TrendingUp, title: '4. Improve Your Mix', desc: 'Apply the recommendations and elevate your track to professional standards.' }
          ].map((step, idx) => (
            <div key={idx} style={{...styles.card, textAlign: 'center'}}>
              <step.icon size={48} color={theme.amber} style={{margin: '0 auto 1rem'}} />
              <h3 style={{color: theme.offWhite, fontSize: 'clamp(1rem, 2.5vw, 1.25rem)', fontWeight: '600', marginBottom: '0.75rem'}}>{step.title}</h3>
              <p style={{color: theme.silver, fontSize: 'clamp(0.875rem, 2vw, 1rem)', lineHeight: '1.6', margin: 0}}>{step.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* What Makes Us Different (comparison table) */}
<div style={styles.section} id="comparison">
  <h2 style={{fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 'bold', color: theme.offWhite, textAlign: 'center', marginBottom: '1rem'}}>
    What Makes Us Different
  </h2>
  <p style={{color: theme.silver, fontSize: 'clamp(0.875rem, 2vw, 1rem)', textAlign: 'center', marginBottom: '3rem', maxWidth: '800px', margin: '0 auto 3rem'}}>
    See how SoniqLab compares to ChatGPT, professional engineers, and generic AI tools
  </p>
  <div style={{...styles.card, overflowX: 'auto'}}>
    <table style={{width: '100%', borderCollapse: 'collapse', minWidth: '800px'}}>
      <thead>
        <tr style={{borderBottom: `2px solid ${theme.border}`}}>
          <th style={{padding: '1rem', textAlign: 'left', color: theme.offWhite, fontSize: 'clamp(0.875rem, 2vw, 1rem)'}}>Feature</th>
          <th style={{padding: '1rem', textAlign: 'center', color: theme.slate, fontSize: 'clamp(0.875rem, 2vw, 1rem)', fontWeight: 'bold'}}>SoniqLab</th>
          <th style={{padding: '1rem', textAlign: 'center', color: theme.silver, fontSize: 'clamp(0.875rem, 2vw, 1rem)'}}>ChatGPT</th>
          <th style={{padding: '1rem', textAlign: 'center', color: theme.silver, fontSize: 'clamp(0.875rem, 2vw, 1rem)'}}>Pro Engineer</th>
          <th style={{padding: '1rem', textAlign: 'center', color: theme.silver, fontSize: 'clamp(0.875rem, 2vw, 1rem)'}}>Generic AI</th>
        </tr>
      </thead>
      <tbody>
        {[
          ['Can analyze audio files', <Check size={20} color={theme.success} />, <X size={20} color={theme.danger} />, <Check size={20} color={theme.success} />, 'Limited'],
          ['Price per song', '$2', 'Free*', '$200-500+', '$10-50'],
          ['Turnaround time', 'Minutes', 'Instant†', '1-2 weeks', 'Varies'],
          ['Frequency band analysis', '100 bands', '0 bands', 'Manual', 'Basic'],
          ['Professional recommendations', '30-50', '10-20‡', 'Varies', 'Limited'],
          ['Auto-analyzed DAW instructions', <Check size={20} color={theme.success} />, <X size={20} color={theme.danger} />, <X size={20} color={theme.danger} />, <X size={20} color={theme.danger} />],
          ['Vocal analysis', <Check size={20} color={theme.success} />, <X size={20} color={theme.danger} />, <Check size={20} color={theme.success} />, <X size={20} color={theme.danger} />],
          ['Rhythm detection', <Check size={20} color={theme.success} />, <X size={20} color={theme.danger} />, <Check size={20} color={theme.success} />, <X size={20} color={theme.danger} />],
          ['Analyzes your actual audio', <Check size={20} color={theme.success} />, <X size={20} color={theme.danger} />, <Check size={20} color={theme.success} />, <X size={20} color={theme.danger} />],
          ['Downloadable report', <Check size={20} color={theme.success} />, <X size={20} color={theme.danger} />, <X size={20} color={theme.danger} />, 'Limited'],
          ['Learn while you work', <Check size={20} color={theme.success} />, 'Depends', 'Depends', <X size={20} color={theme.danger} />],
          ['Keep creative control', <Check size={20} color={theme.success} />, <Check size={20} color={theme.success} />, <X size={20} color={theme.danger} />, <Check size={20} color={theme.success} />],
          ['No file storage', <Check size={20} color={theme.success} />, <Check size={20} color={theme.success} />, <X size={20} color={theme.danger} />, <X size={20} color={theme.danger} />]
        ].map((row, idx) => (
          <tr key={idx} style={{borderBottom: `1px solid ${theme.border}`}}>
            <td style={{padding: '1rem', color: theme.offWhite, fontSize: 'clamp(0.875rem, 2vw, 1rem)'}}>{row[0]}</td>
            <td style={{padding: '1rem', textAlign: 'center', color: theme.offWhite, fontSize: 'clamp(0.875rem, 2vw, 1rem)', fontWeight: '600', display: 'flex', justifyContent: 'center'}}>{row[1]}</td>
            <td style={{padding: '1rem', textAlign: 'center', color: theme.silver, fontSize: 'clamp(0.875rem, 2vw, 1rem)'}}>{row[2]}</td>
            <td style={{padding: '1rem', textAlign: 'center', color: theme.silver, fontSize: 'clamp(0.875rem, 2vw, 1rem)'}}>{row[3]}</td>
            <td style={{padding: '1rem', textAlign: 'center', color: theme.silver, fontSize: 'clamp(0.875rem, 2vw, 1rem)'}}>{row[4]}</td>
          </tr>
        ))}
      </tbody>
    </table>
    
    {/* Footnotes */}
    <div style={{marginTop: '1.5rem', padding: '1rem', background: theme.charcoal, borderRadius: '0.5rem'}}>
      <p style={{color: theme.slate, fontSize: '0.75rem', margin: '0.25rem 0', lineHeight: '1.4'}}>
        * Free but requires you to manually extract and provide all audio data
      </p>
      <p style={{color: theme.slate, fontSize: '0.75rem', margin: '0.25rem 0', lineHeight: '1.4'}}>
        † Instant responses but only after you provide analysis data
      </p>
      <p style={{color: theme.slate, fontSize: '0.75rem', margin: '0.25rem 0', lineHeight: '1.4'}}>
        ‡ Generic advice without specific measurements or parameters
      </p>
      <p style={{color: theme.slate, fontSize: '0.75rem', margin: '0.25rem 0', lineHeight: '1.4'}}>
        § ChatGPT can give DAW instructions, but only after you manually analyze your track with separate tools and provide all the data
      </p>
    </div>
  </div>
</div>

      {/* Key Benefits Section */}
      <div style={styles.section} id="benefits">
        <h2 style={{fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 'bold', color: theme.offWhite, textAlign: 'center', marginBottom: '3rem'}}>
          Key Benefits
        </h2>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem'}}>
          {[
            { icon: Shield, title: 'Keep Creative Control', desc: 'You make the final decisions. Our AI provides insights, not mandates.' },
            { icon: BookOpen, title: 'Learn As You Go', desc: 'Understand why changes are recommended. Build your mixing skills over time.' },
            { icon: DollarSign, title: 'Affordable Excellence', desc: 'Professional-grade analysis for $2. No subscriptions, no hidden fees.' },
            { icon: Award, title: 'Studio-Grade Insights', desc: 'Industry-standard measurements: LUFS, dynamic range, frequency analysis, and more.' },
            { icon: Target, title: 'DAW-Specific Instructions', desc: 'Exact plugin settings and step-by-step guides for your specific DAW.' },
            { icon: Clock, title: 'Instant Results', desc: 'Get your analysis in minutes. Download and reference anytime, forever.' }
          ].map((benefit, idx) => (
            <div key={idx} style={styles.card}>
              <benefit.icon size={40} color={theme.amber} style={{marginBottom: '1rem'}} />
              <h3 style={{color: theme.offWhite, fontSize: 'clamp(1rem, 2.5vw, 1.125rem)', fontWeight: '600', marginBottom: '0.75rem'}}>{benefit.title}</h3>
              <p style={{color: theme.silver, fontSize: 'clamp(0.875rem, 2vw, 1rem)', lineHeight: '1.6', margin: 0}}>{benefit.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Trust & Transparency */}
      <div style={styles.section} id="trust">
        <h2 style={{fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 'bold', color: theme.offWhite, textAlign: 'center', marginBottom: '3rem'}}>
          Your Music, Your Rights
        </h2>
        <p style={{textAlign: 'center', margin: '-2rem 0 2.5rem'}}>
          <a href="/privacy.html" style={{color: theme.amber}}>Read our privacy policy</a>
        </p>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem'}}>
          {[
            { icon: Shield, title: 'Zero Data Retention', desc: 'Your audio files are deleted immediately after analysis. We never store your music.' },
            { icon: Award, title: 'You Own Everything', desc: 'We claim no rights to your music, mixes, or creative decisions. Your art is yours alone.' },
            { icon: CheckCircle2, title: 'Secure Processing', desc: 'All payments processed through Stripe. Your payment information is never stored on our servers.' },
            { icon: BookOpen, title: 'Transparent Analysis', desc: 'See exactly what we analyze and why. No black box algorithms—just clear, actionable insights.' }
          ].map((item, idx) => (
            <div key={idx} style={{...styles.card, textAlign: 'center'}}>
              <item.icon size={48} color={theme.success} style={{margin: '0 auto 1rem'}} />
              <h3 style={{color: theme.offWhite, fontSize: 'clamp(1rem, 2.5vw, 1.125rem)', fontWeight: '600', marginBottom: '0.75rem'}}>{item.title}</h3>
              <p style={{color: theme.silver, fontSize: 'clamp(0.875rem, 2vw, 1rem)', lineHeight: '1.6', margin: 0}}>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div style={{background: theme.charcoal, borderTop: `1px solid ${theme.border}`, padding: '2rem', textAlign: 'center'}}>
        <p style={{color: theme.silver, fontSize: 'clamp(0.875rem, 2vw, 1rem)', margin: 0}}>
          © {new Date().getFullYear()} SoniqLab. All rights reserved.
        </p>
        <p style={{fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)', marginTop: '0.5rem'}}>
          <a href="/privacy.html" style={{color: theme.amber}}>Privacy policy</a>
        </p>
        <p style={{color: theme.slate, fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)', marginTop: '0.5rem'}}>
          Professional AI audio engineering analysis • $2 per song
        </p>
      </div>

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        input:focus { outline: none; border-color: ${theme.amber}; }
        button:hover:not(:disabled) { opacity: 0.9; }
        button:disabled { opacity: 0.5; cursor: not-allowed; }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          table {
            font-size: 0.75rem;
          }
          table th, table td {
            padding: 0.5rem !important;
          }
        }

        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }
      `}</style>
    </div>
  );
}

// Main App component wrapped with Stripe Elements provider
export default function AudioEngineerApp() {
  return (
    <Elements stripe={stripePromise}>
      <AudioAnalyzerForm />
    </Elements>
  );
}